package fr.manyverse.app.presentation.screens

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.presentation.components.CharacterEditForm
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.CharacterViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.edit_character
import manyverse.shared.generated.resources.error_loading_characters
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CharacterEditScreen(
    userId: String,
    characterId: String,
    onNavigateBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: CharacterViewModel = koinViewModel()
) {
    val characterListState by viewModel.characterListState.collectAsStateWithLifecycle()

    // Load characters if not already loaded
    LaunchedEffect(userId) {
        if (characterListState !is AsyncUiState.Success) {
            viewModel.loadAllCharacters(userId)
        }
    }

    // Find the character to edit
    val character = remember(characterListState, characterId) {
        if (characterListState is AsyncUiState.Success) {
            (characterListState as AsyncUiState.Success).data.find { it.id == characterId }
        } else {
            null
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.edit_character)) },
                navigationIcon = {
                    IconButton(
                        onClick = onNavigateBack,
                        modifier = Modifier.testTag("backButton")
                    ) {
                        Icon(
                            Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = stringResource(Res.string.back)
                        )
                    }
                }
            )
        },
        modifier = modifier
    ) { padding ->
        when {
            characterListState is AsyncUiState.Loading -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(padding)
                        .testTag("loadingBox"),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(
                        modifier = Modifier.testTag("loadingIndicator")
                    )
                }
            }

            character == null -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(padding)
                        .testTag("errorBox"),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = stringResource(Res.string.error_loading_characters),
                        style = MaterialTheme.typography.titleMedium,
                        color = MaterialTheme.colorScheme.error,
                        modifier = Modifier.testTag("errorText")
                    )
                }
            }

            else -> {
                CharacterEditForm(
                    userId = userId,
                    character = character,
                    viewModel = viewModel,
                    onSaved = onNavigateBack,
                    onCancel = onNavigateBack,
                    modifier = Modifier
                        .padding(padding)
                        .safeContentPadding()
                )
            }
        }
    }
}
